import React, { useContext, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import ClientsApi from 'api/ClientsApi';

import { AuthContext } from 'Auth/AuthContext';

import RoundedButton from 'components/RoundedButton/RoundedButton';

import EditSubscriptionModal from 'Customers/Subscriptions/components/EditSubscriptionModal/EditSubscriptionModal';

import { PACKAGE_NAMES } from 'Customers/constants';

import BillingCapacityDetails from './BillingCapacityDetails';

import BillingCapacityMove, { capacityChanged } from './BillingCapacityMove';

import { canMoveBilling } from './billingPermissions';

export default function DetailsBillingMove({ tenantId, summary }) {
  const { authState } = useContext(AuthContext) || {};
  const queryClient = useQueryClient();
  const available = !summary || summary.originalMovePending || ['legacy', 'setup_pending'].includes(summary.mode);
  const allowed = canMoveBilling(authState?.user);
  const [target, setTarget] = useState(null);
  const [lookup, setLookup] = useState('loading');
  const [retry, setRetry] = useState(0);
  const [move, setMove] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [purchaseReceipt, setPurchaseReceipt] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let current = true;
    setTarget(null);
    setLookup('loading');
    if (!available || !allowed || !tenantId)
      return () => {
        current = false;
      };
    (async () => {
      const items = [];
      let total;
      let pages;
      const readPage = async (page) => {
        const response = await ClientsApi.getTenantSubscriptions(tenantId, { currentPage: page, perPage: 100 });
        const { data } = response;
        if (
          !Array.isArray(data.items) ||
          !Number.isInteger(data.pagination?.total) ||
          !Number.isInteger(data.pagination?.pages) ||
          data.pagination.pages < 1
        )
          throw Error('Incomplete list');
        if (page === 1) {
          total = data.pagination.total;
          pages = data.pagination.pages;
        }
        if (total !== data.pagination.total || pages !== data.pagination.pages) throw Error('Changed list');
        items.push(...data.items);
        if (page < pages) await readPage(page + 1);
      };
      await readPage(1);
      if (items.length !== total) throw Error('Incomplete list');
      const candidates = items.filter((row) => row.isPrimary && row.status === 'ACTIVE');
      if (!current) return;
      if (candidates.length !== 1 || !candidates[0].id) {
        setLookup('unavailable');
        return;
      }
      setTarget({ ...candidates[0], resolvedTenantId: tenantId });
      setLookup('ready');
    })().catch(() => {
      if (current) setLookup('unavailable');
    });
    return () => {
      current = false;
    };
  }, [tenantId, available, allowed, retry]);
  return (
    <>
      <BillingCapacityDetails summary={summary}>
        {available &&
          allowed &&
          (lookup === 'ready' && target?.resolvedTenantId === tenantId ? (
            <RoundedButton
              text={
                summary?.originalMovePending || summary?.mode === 'setup_pending'
                  ? 'Setup pending'
                  : 'Move to active-access billing'
              }
              color='primary'
              size='sm'
              onClick={() => {
                setPurchaseReceipt(null);
                setRefresh(0);
                setMove(target);
              }}
            />
          ) : lookup === 'unavailable' ? (
            <div>
              <p>Move review unavailable: a single current primary subscription could not be identified.</p>
              <RoundedButton
                text='Retry subscription lookup'
                color='white'
                size='sm'
                onClick={() => setRetry((value) => value + 1)}
              />
            </div>
          ) : (
            <p>Loading subscription…</p>
          ))}
      </BillingCapacityDetails>
      {move && (
        <>
          <BillingCapacityMove
            key={move.id}
            subscriptionId={move.id}
            plan={PACKAGE_NAMES[move.plan] || move.plan}
            billingCapacity={move.billingCapacity}
            initiallyOpen
            hideTrigger
            purchaseReceipt={purchaseReceipt}
            refreshKey={refresh}
            onEdit={setEditPreview}
            onDismiss={() => setMove(null)}
            onSuccess={() => {
              capacityChanged();
              queryClient.invalidateQueries({ queryKey: ['TenantSubscriptions'] });
            }}
          />
          <EditSubscriptionModal
            isOpen={!!editPreview}
            selected={move}
            billingPreview={editPreview}
            onPurchaseComplete={setPurchaseReceipt}
            onClose={() => {
              setEditPreview(null);
              setRefresh((value) => value + 1);
            }}
          />
        </>
      )}
    </>
  );
}
